/**
 * SelectAdvertiserModal Component
 * Modal for selecting an advertiser before starting a new chat
 */

import { useState } from 'react'
import { X, MessageSquarePlus } from 'lucide-react'
import { AdvertiserSelect } from '../shared/AdvertiserSelect'
import { Advertiser } from '../../lib/api/advertisers'

interface SelectAdvertiserModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (advertiser: Advertiser) => void
}

export function SelectAdvertiserModal({
  isOpen,
  onClose,
  onConfirm
}: SelectAdvertiserModalProps) {
  const [selectedAdvertiser, setSelectedAdvertiser] = useState<Advertiser | null>(null)

  if (!isOpen) return null

  const handleConfirm = () => {
    if (selectedAdvertiser) {
      onConfirm(selectedAdvertiser)
      setSelectedAdvertiser(null)
    }
  }

  const handleClose = () => {
    setSelectedAdvertiser(null)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-gray-950 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950 rounded-t-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                <MessageSquarePlus className="h-3 w-3" />
                New chat
              </div>
              <h2 className="text-2xl font-bold mt-1">Select an advertiser</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Choose the advertiser this conversation will be associated with.
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-full bg-white dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shadow-sm"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-visible">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Advertiser
          </label>
          <AdvertiserSelect
            value={selectedAdvertiser}
            onChange={setSelectedAdvertiser}
            placeholder="Search and select an advertiser..."
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            All conversations and campaigns will be associated with this advertiser.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 rounded-b-2xl">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedAdvertiser}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <MessageSquarePlus className="w-4 h-4" />
            Start Chat
          </button>
        </div>
      </div>
    </div>
  )
}

